import { Alert, Button, Drawer, message } from 'antd';
import React, { useState, useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { metricCategoryMapping } from '@pulse/metrics';
import { useSelector } from 'react-redux';
import { aiAlertsCustomMetricsLimit } from '@src/store/common/selectors';
import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import { METRIC_POPUP_OK_TEXT, PRODUCT_MODE_MAP } from '@utils/constants';
import {
  drawerMetricsAtom,
  fullSensitivityConfigAtom,
} from '@src/components/aiAlerts/sensitivityControl/atoms';
import useMetricCategory from '@src/components/common/MetricSelectorPopup/hooks/useMetricCategory';
import { prepareDefaultSensitivityConfig, aiAlertMapping } from '@src/utils/aiAlerts/metrics';
import {
  CountText,
  FlexFill,
  FooterWrapper,
  RowWrapper,
} from '@src/components/common/MetricSelectorPopup/styled';
import useMetricCategories from '@src/components/common/MetricSelectorPopup/hooks/useMetricCategories';
import metricsStore from '@globalStore/metricsStore';
import CheckboxGroup from '@src/components/common/MetricSelectorPopup/CheckboxGroup';
import ModalAnchor from '@src/components/common/MetricSelectorPopup/MetricAnchor';
import useMetricCategoryOrder from '@src/components/common/MetricSelectorPopup/hooks/useMetricCategoryOrder';
import { useRefTags } from '@src/hooks';
import ModalTitleWithSearch from '@src/components/common/MetricSelectorPopup/ModalTitleSearch';
import { ContentWrapper, RightPanel } from '../styled';
import { contextTypeAtom } from '@src/atoms/page';

const Notice = () => {
  return (
    <div>
      For best results, choose metrics which have:
      <ul style={{ fontSize: 14, margin: '0 0 5px -20px' }}>
        <li>A stable baseline during normal behavior</li>
        <li>Significant volume (at least 25 devices per minute)</li>
        <li>Spike increases on degradation (failure rate, not success rate)</li>
      </ul>
      <span>
        Depending on traffic levels, new or updated metric definitions take up to 4 days to
        establish their new baseline.
      </span>
    </div>
  );
};

const MetricsDrawer = ({ isDrawerOpen, onBack, onClose, searchRef, setOpenMetricDrawer }) => {
  const [drawerMetrics, setDrawerMetrics] = useAtom(drawerMetricsAtom);
  const setContextType = useSetAtom(contextTypeAtom);
  const [isLoading, setIsLoading] = useState(false);
  const aiAlertsMetricsCustomMetricsLimit = useSelector(aiAlertsCustomMetricsLimit);
  const [sensitivityDetails, setSensitivityDetails] = useAtom(fullSensitivityConfigAtom);
  const metricsMeta = metricsStore.getState().combinedMetricsMeta;
  const activeMetricsMeta = metricsStore.getState().combinedMetricsMeta;

  useEffect(() => {
    // can be replaced by tab/product type when all tabs/products will have editable alert metrics
    setContextType('APP');
  }, [setContextType]);

  const excludeCategories = [metricCategoryMapping.APP_EXPERIENCE, metricCategoryMapping.QOE];
  const metricCategoryOrder = useMetricCategoryOrder({
    selectedMetrics: drawerMetrics,
  });
  const metricCategory = useMetricCategory({ selectedMetrics: drawerMetrics });
  const currentMetricCategoryOrder = metricCategoryOrder[PRODUCT_MODE_MAP.APP];
  const currentMetricCategory = metricCategory[PRODUCT_MODE_MAP.APP];
  const { refs, setRefFn } = useRefTags(currentMetricCategoryOrder);
  const [searchText, setSearchText] = useState('');

  const metricCategories = useMetricCategories({
    supportedMetrics: activeMetricsMeta.app,
    selectedMetricList: drawerMetrics,
    lockedMetricsSet: new Set(),
    excludeCategories,
    allMetrics: metricsMeta.app,
  });

  const onApply = () => {
    setIsLoading(true);
    const aiAlertsMappingKeys = Object.keys(aiAlertMapping);
    const sensitivityForCustomMetrics = drawerMetrics.map((metric) => {
      const sensitivityConfig = sensitivityDetails.find(
        (sensitivity) => sensitivity.metric === metric,
      );
      if (sensitivityConfig) {
        return sensitivityConfig;
      } else {
        return prepareDefaultSensitivityConfig(metric, metric, PRODUCT_MODE_MAP.APP);
      }
    });
    const filteredSensitivity = sensitivityDetails.filter((sensitivity) => {
      return aiAlertsMappingKeys.includes(sensitivity.metric);
    });
    setSensitivityDetails([...filteredSensitivity, ...sensitivityForCustomMetrics]);
    setTimeout(() => {
      setIsLoading(false);
      setOpenMetricDrawer(false);
    }, 1000);
  };

  const getInputListFn = (metricsList: any = []) => {
    if (searchText) {
      const st = searchText.toLowerCase();
      return metricsList.filter((metric) => metric.name.toLowerCase().includes(st));
    }
    return metricsList;
  };

  const metricCount = drawerMetrics.length;
  const footerButtons = [
    <FooterWrapper key="footer-wrapper">
      <CountText>{`${metricCount} ${metricCount > 1 ? 'metrics' : 'metric'} selected`}</CountText>
      <FlexFill />
      <Button
        key="discard"
        onClick={onBack}
        style={{
          marginRight: '10px',
        }}
      >
        Cancel
      </Button>
      <Button type="primary" key="save" loading={isLoading} onClick={onApply}>
        {METRIC_POPUP_OK_TEXT}
      </Button>
    </FooterWrapper>,
  ];

  const onChange = (item) => {
    let listOfMetrics: string[] = [];
    const metricName = item.target.value;
    listOfMetrics.push(...drawerMetrics);
    if (listOfMetrics.includes(metricName)) {
      listOfMetrics = listOfMetrics.filter((value) => value !== metricName);
    } else {
      listOfMetrics.push(metricName);
    }
    setDrawerMetrics(listOfMetrics);
  };

  // skip excluded categories
  const categoryOrder = currentMetricCategoryOrder.filter(
    (categoryKey) => currentMetricCategory[categoryKey] in metricCategories,
  );

  useEffect(() => {
    if (drawerMetrics.length > aiAlertsMetricsCustomMetricsLimit) {
      message.warning(`You can select a maximum of ${aiAlertsMetricsCustomMetricsLimit} metrics.`);
      setDrawerMetrics((prev) => prev.slice(0, aiAlertsMetricsCustomMetricsLimit));
    }
  }, [drawerMetrics]);

  return (
    <Drawer
      width={window.innerWidth > 1000 ? 1000 : window.innerWidth - 100}
      extra={<CloseOutlined onClick={onClose} />}
      open={isDrawerOpen}
      headerStyle={{ borderBottom: 0, display: 'none' }}
      bodyStyle={{ padding: 0 }}
      footer={footerButtons}
      forceRender={true}
    >
      <ContentWrapper>
        <div style={{ backgroundColor: '#F6F6F6' }}>
          <ArrowLeftOutlined onClick={onBack} style={{ padding: '20px', fontSize: 20 }} />
          <ModalAnchor
            navRefs={refs.current}
            categoryOrder={categoryOrder}
            listCategory={metricCategory}
            style={{ backgroundColor: '#F6F6F6' }}
          />
        </div>
        <RightPanel>
          <div style={{ fontSize: 18, color: 'gray', margin: '20px 10px 12px -10px' }}>
            <ModalTitleWithSearch
              placeholder="Search metric"
              modalTitle="Select metrics"
              searchText={searchText}
              setSearchText={setSearchText}
              searchRef={searchRef}
            />
          </div>

          <Alert style={{ margin: '10px 20px 10px 10px' }} message={<Notice />} type="info" />

          {categoryOrder.map((categoryKey) => {
            return (
              <RowWrapper key={categoryKey}>
                <CheckboxGroup
                  categoryKey={categoryKey}
                  listCategory={metricCategory}
                  inputList={getInputListFn(metricCategories[currentMetricCategory[categoryKey]])}
                  onChange={onChange}
                  setRef={setRefFn(categoryKey)}
                  emptyText={<>No Metrics available</>}
                  onBulkChange={undefined}
                  tempKpi={undefined}
                  setTempKpi={undefined}
                  hasSearchText={undefined}
                />
              </RowWrapper>
            );
          })}
        </RightPanel>
      </ContentWrapper>
    </Drawer>
  );
};

export default MetricsDrawer;
